/**
 * @todo Use `@headless/ui` v2 after upgrade to React 18
 * @see {@link https://catalyst.tailwindui.com/docs/dialog|Catalyst Dialog}
 */
import type React from 'react';
import { Fragment } from 'react';
import * as Headless from '@headlessui/react';

import { cls } from '@/utils/cls';

import { Text } from './text';

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
};

export type DialogProps = {
  size?: keyof typeof sizes;
  className?: string;
  children: React.ReactNode;
} & Omit<Headless.DialogProps<'div'>, 'className'>;

export function Dialog({
  open,
  onClose,
  size = 'lg',
  className,
  children,
  ...props
}: DialogProps) {
  return (
    <Headless.Transition as={Fragment} appear show={open} {...props}>
      <Headless.Dialog onClose={onClose}>
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center">
            <Headless.Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Headless.Dialog.Overlay className="fixed inset-0 bg-black/30" />
            </Headless.Transition.Child>

            <Headless.Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
              className={cls(
                'w-full transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all',
                sizes[size],
                className,
              )}
            >
              <Headless.Dialog.Panel>{children}</Headless.Dialog.Panel>
            </Headless.Transition.Child>
          </div>
        </div>
      </Headless.Dialog>
    </Headless.Transition>
  );
}

export function DialogTitle<T extends React.ElementType>({
  className,
  ...props
}: { className?: string } & Omit<Headless.DialogTitleProps<T>, 'className'>) {
  return (
    <Headless.Dialog.Title
      {...props}
      className={cls(
        className,
        'text-balance text-lg/6 font-semibold text-zinc-950 sm:text-base/6',
      )}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<'p'>,
  'className'
>) {
  return (
    <Headless.Dialog.Description
      as={Text}
      {...props}
      className={cls(className, 'mt-2 text-pretty')}
    />
  );
}

export function DialogBody({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={cls(className, 'mt-6')} />;
}

export function DialogActions({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={cls(
        'mt-8 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto',
        className,
      )}
    />
  );
}
